import { useCallback, useEffect, useState } from "react";
import { CheckCircle, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageCircle, Mail } from "lucide-react";
import { buildLeadWhatsAppUrl } from "@/lib/lead-whatsapp";
import { apiGetJson, clientRequestHeaders, ensureCsrfToken } from "@/lib/sm-api";
import type { OnboardingPaymentAttempt } from "@/lib/onboarding-payment-attempts";
import type { OnboardingSession } from "@/lib/self-service-provisioning";

type LeadDetail = {
  id: string;
  nombre: string;
  negocio: string;
  email: string;
  telefono: string;
  plan_interesado: string;
  status: string;
  created_at: string;
  form_data?: Record<string, unknown> | null;
};

interface LeadDetailResponse {
  lead: LeadDetail;
  session: OnboardingSession | null;
  paymentAttempts: OnboardingPaymentAttempt[];
  attemptCount: number;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("es-AR");
}

function fmtMoney(cents: number | null, currency: string | null) {
  if (cents == null) return "—";
  const cur = currency ?? "USD";
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: cur }).format(cents / 100);
}

const FORM_DATA_LABELS: Record<string, string> = {
  source: "Origen",
  businessProfile: "Rubro",
  businessSlug: "Slug de la tienda",
  paymentProvider: "Pasarela de pago",
  billingMode: "Modalidad de facturación",
  currency: "Moneda",
  amountCents: "Monto",
  ownerDni: "DNI",
  rubro: "Rubro",
  onboardingStatus: "Estado del onboarding",
  syncedAt: "Última sincronización",
  lastError: "Último error",
};

function fmtFormValue(key: string, value: unknown): string {
  if (value == null || value === "") return "—";
  if (key === "amountCents" && typeof value === "number") return `${(value / 100).toFixed(2)}`;
  if (key === "syncedAt" && typeof value === "string") return fmtDate(value);
  return String(value);
}

/** Vista renderizada del metadata — antes era un JSON crudo sin traducir. */
function FormDataDetails({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) => v != null && v !== "");
  if (entries.length === 0) return null;
  return (
    <div className="grid gap-1.5 text-xs sm:grid-cols-2">
      {entries.map(([key, value]) => (
        <div key={key} className="flex justify-between gap-2 rounded-md border border-border/40 bg-secondary/10 px-2 py-1">
          <span className="text-muted-foreground">{FORM_DATA_LABELS[key] ?? key}</span>
          <span className="font-medium text-right">{fmtFormValue(key, value)}</span>
        </div>
      ))}
    </div>
  );
}

export function LeadDetailDialog({
  leadId,
  open,
  onOpenChange,
}: {
  leadId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [data, setData] = useState<LeadDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const r = await apiGetJson<LeadDetailResponse>(`/api/leads?id=${encodeURIComponent(leadId)}`);
      setData(r);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (open && leadId) void load();
  }, [open, leadId, load]);

  const act = async (sessionId: string, action: string) => {
    setActing(action);
    try {
      await ensureCsrfToken();
      const res = await fetch("/api/admin/onboarding", {
        method: "POST",
        headers: { ...clientRequestHeaders(), "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sessionId, action }),
      });
      const d = (await res.json()) as Record<string, unknown>;
      if (!res.ok) throw new Error(String(d.error ?? "Error"));
      toast.success(action === "provision" ? `Tenant ${d.tenantId} provisionado` : "OK");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setActing(null);
    }
  };

  const lead = data?.lead;
  const formData = (lead?.form_data ?? {}) as Record<string, unknown>;
  const session = data?.session;
  const attempts = data?.paymentAttempts ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalle del lead</DialogTitle>
          <DialogDescription>
            Datos cargados, sesión de onboarding e historial de intentos de pago.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : !lead ? (
          <p className="text-sm text-muted-foreground">No se pudo cargar el lead.</p>
        ) : (
          <div className="space-y-4">
            <section className="space-y-1">
              <p className="text-sm font-semibold">{lead.negocio}</p>
              <p className="text-xs text-muted-foreground">
                {lead.nombre} · {lead.email} · {lead.telefono}
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Badge variant="outline">{lead.plan_interesado}</Badge>
                <Badge>{lead.status}</Badge>
                <Badge variant="secondary">{fmtDate(lead.created_at)}</Badge>
              </div>
              {/* Acciones de contacto */}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button size="sm" variant="outline" className="gap-1.5" asChild>
                  <a
                    href={buildLeadWhatsAppUrl({
                      nombre: lead.nombre, negocio: lead.negocio, email: lead.email,
                      telefono: lead.telefono, planInteresado: lead.plan_interesado,
                    })}
                    target="_blank" rel="noreferrer"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </a>
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" asChild>
                  <a
                    href={`mailto:${lead.email}?subject=${encodeURIComponent(`Tu solicitud en Structura Market — ${lead.negocio}`)}&body=${encodeURIComponent(`Hola ${lead.nombre},\n\nGracias por tu interés en Structura Market para ${lead.negocio}. Te escribo para ayudarte a activar tu tienda online.\n\n¿Cuándo te queda cómodo para coordinar?\n\nSaludos.`)}`}
                  >
                    <Mail className="h-3.5 w-3.5" /> Email recordatorio
                  </a>
                </Button>
              </div>
            </section>

            {Object.keys(formData).length > 0 ? (
              <section>
                <p className="mb-2 text-sm font-medium">Formulario / metadata</p>
                <FormDataDetails data={formData} />
              </section>
            ) : null}

            {session ? (
              <section className="rounded-lg border border-border/60 bg-secondary/10 p-3 space-y-2">
                <p className="text-sm font-medium">Sesión onboarding</p>
                <div className="grid gap-1 text-xs sm:grid-cols-2">
                  <span>ID: <code className="text-[10px]">{session.id}</code></span>
                  <span>Estado: <strong>{session.status}</strong></span>
                  <span>Plan: {session.planId} · {session.billingMode}</span>
                  <span>Pasarela: {session.paymentProvider ?? "—"}</span>
                  <span>Monto: {fmtMoney(session.amountCents, session.currency)}</span>
                  <span>Slug: {session.businessSlug ?? "—"}</span>
                </div>
                {session.errorMessage ? (
                  <p className="text-xs text-red-600">⚠ {session.errorMessage}</p>
                ) : null}
                <div className="flex flex-wrap gap-2 pt-1">
                  {session.status === "payment_pending" && (
                    <>
                      <Button
                        size="sm" variant="outline" className="gap-1.5 text-xs h-7"
                        disabled={acting === "confirm_payment"}
                        onClick={() => void act(session.id, "confirm_payment")}
                      >
                        {acting === "confirm_payment"
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <CheckCircle className="h-3 w-3" />}
                        Confirmar pago
                      </Button>
                      <Button
                        size="sm" variant="outline" className="gap-1.5 text-xs h-7"
                        disabled={acting === "expire"}
                        onClick={() => void act(session.id, "expire")}
                      >
                        Expirar sesión
                      </Button>
                    </>
                  )}
                  {(session.status === "payment_confirmed" || session.status === "failed") && !session.tenantId && (
                    <Button
                      size="sm" className="gap-1.5 text-xs h-7"
                      disabled={acting === "provision"}
                      onClick={() => void act(session.id, "provision")}
                    >
                      {acting === "provision"
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <ShieldCheck className="h-3 w-3" />}
                      Provisionar tenant
                    </Button>
                  )}
                  {session.status === "failed" && (
                    <Button
                      size="sm" variant="outline" className="gap-1.5 text-xs h-7"
                      disabled={acting === "reset_failed"}
                      onClick={() => void act(session.id, "reset_failed")}
                    >
                      Reintentar
                    </Button>
                  )}
                </div>
              </section>
            ) : formData.onboardingSessionId ? (
              <p className="text-xs text-muted-foreground">Sesión vinculada no encontrada.</p>
            ) : null}

            <section>
              <p className="mb-2 text-sm font-medium">
                Intentos de pago ({data?.attemptCount ?? attempts.length})
              </p>
              {attempts.length === 0 ? (
                <p className="text-xs text-muted-foreground">Sin intentos registrados.</p>
              ) : (
                <div className="max-h-56 space-y-1 overflow-auto rounded-lg border border-border/60">
                  {attempts.map((a) => (
                    <div
                      key={a.id}
                      className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 px-2 py-1.5 text-xs last:border-0"
                    >
                      <span>
                        <strong>{a.provider}</strong> · {a.status}
                        {a.externalId ? ` · ${a.externalId}` : ""}
                      </span>
                      <span className="flex items-center gap-2 text-muted-foreground">
                        {fmtMoney(a.amountCents, a.currency)}
                        {fmtDate(a.createdAt)}
                      </span>
                      {a.errorMessage ? (
                        <span className="w-full text-red-600">{a.errorMessage}</span>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
